import clsx from 'clsx';

import { useConfigContext } from '../config-wrapper';

import { FeedBackOptions, useFeedbackContext } from './feedback-context';
import { FeedbackIconPositiv } from './icons/feedback-positive';
import { FeedbackIconNegative } from './icons/feedback-negative';

import './answer-footer.less';

// type of component props
type AnswerFooterProps = {
  question: number;
  printButton?: JSX.Element;
};

/**
 * Answer footer component.
 * Lets user give feedback to given question.
 *
 * @param {AnswerFooterProps} props - component props
 *
 * @returns {JSX.Element}
 */
export function AnswerFooter({ question, printButton }: AnswerFooterProps): JSX.Element {
  // get contexts
  const config = useConfigContext();
  const { choices, trackFeedback } = useFeedbackContext();

  if (!config.isFeedbackActive && !printButton) {
    return null;
  }

  // get choice for current question
  const choiceForQuestion = choices.find((choice) => choice.question === question);

  // get feedback option for choice
  const selected = choiceForQuestion != null ? choiceForQuestion.feedback : null;

  // render component
  return (
    <div className={config.generateClassName('feedback')}>
      {config.isFeedbackActive ? (
        <>
          <label className={config.generateClassName('feedback__label')}>
            {config.loc('feedback')}
          </label>
          <div className={config.generateClassName('feedback__actions')}>
            <div
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  trackFeedback(question, FeedBackOptions.FEEDBACK_POSITIVE);
                }
              }}
              onClick={() => trackFeedback(question, FeedBackOptions.FEEDBACK_POSITIVE)}
              aria-label={config.loc('aria_positive_feedback')}
              className={clsx(
                config.generateClassName('feedback__action'),
                config.generateClassName('feedback__action--positive'),
                {
                  [config.generateClassName('feedback__action--selected')]:
                    selected === FeedBackOptions.FEEDBACK_POSITIVE,
                },
              )}>
              {FeedbackIconPositiv}
            </div>
            <div
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  trackFeedback(question, FeedBackOptions.FEEDBACK_NEGATIVE);
                }
              }}
              onClick={() => trackFeedback(question, FeedBackOptions.FEEDBACK_NEGATIVE)}
              aria-label={config.loc('aria_negative_feedback')}
              className={clsx(
                config.generateClassName('feedback__action'),
                config.generateClassName('feedback__action--negative'),
                {
                  [config.generateClassName('feedback__action--selected')]:
                    selected === FeedBackOptions.FEEDBACK_NEGATIVE,
                },
              )}>
              {FeedbackIconNegative}
            </div>
          </div>
        </>
      ) : (
        <div className={config.generateClassName('feedback__actions')}></div>
      )}
      {printButton}
    </div>
  );
}
